import React, { useState, useEffect } from 'react'
import Card from '../../components/Card.jsx'
import * as api from '../../services/api.js'
import { todayISO } from '../../utils/dates.js'
import { COMPANIONS, COMPANION_ORDER, STEP_MILESTONES, MAX_STEPS, GOAL_OPTIONS } from './companionData.js'

const DEFAULT_WALK = { companion: 'carl', goal: 5000, logs: {} }

export default function WalkingChallenge() {
  const [data, setData] = useState(DEFAULT_WALK)
  useEffect(() => { api.getWalkState('walking').then(d => setData({ ...DEFAULT_WALK, ...(d || {}) })) }, [])
  const [stepInput, setStepInput] = useState('')
  const [showPrizes, setShowPrizes] = useState(false)

  const companion = COMPANIONS[data.companion] ?? COMPANIONS.carl
  const today = todayISO()
  const compLog = data.logs?.[companion.id] ?? []
  const todaySteps = (compLog.find(e => e.date === today) ?? { steps: 0 }).steps
  const totalSteps = compLog.reduce((s, e) => s + e.steps, 0)
  const goalPct = Math.min((todaySteps / data.goal) * 100, 100)
  const corridorPct = Math.min((totalSteps / MAX_STEPS) * 100, 100)
  const nextPrize = companion.prizes.find((_, i) => totalSteps < STEP_MILESTONES[i])
  const nextMilestone = STEP_MILESTONES.find(m => totalSteps < m)

  function updateData(updater) {
    setData(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      api.saveWalkState('walking', next)
      return next
    })
  }

  function selectCompanion(id) {
    updateData(prev => ({ ...prev, companion: id }))
    setShowPrizes(false)
  }

  function logWalk(steps) {
    const s = Math.max(0, steps || 0)
    updateData(prev => {
      const existing = (prev.logs?.[companion.id] ?? []).find(e => e.date === today)
      const updated = existing
        ? (prev.logs[companion.id]).map(e => e.date === today ? { ...e, steps: e.steps + s } : e)
        : [...(prev.logs?.[companion.id] ?? []), { date: today, steps: s }]
      return { ...prev, logs: { ...prev.logs, [companion.id]: updated.slice(-365) } }
    })
    setStepInput('')
  }

  function handleGoalChange(e) {
    updateData(prev => ({ ...prev, goal: Number(e.target.value) }))
  }

  return (
    <Card label="Check-in · Activities" title="Walking Challenge">

      {/* ── Companion selector ── */}
      <p style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--stone)', marginBottom: '0.6rem' }}>
        Choose your companion
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.4rem', marginBottom: '1.5rem' }}>
        {COMPANION_ORDER.map(id => {
          const c = COMPANIONS[id]
          const selected = data.companion === id
          return (
            <button
              key={id}
              onClick={() => selectCompanion(id)}
              style={{
                background: c.cardBg,
                border: `2px solid ${selected ? c.selectedBorder : 'transparent'}`,
                borderRadius: '10px', padding: '0.5rem 0.3rem', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
                transition: 'all 0.2s',
                boxShadow: selected ? `0 0 0 1px ${c.selectedBorder}` : 'none',
              }}
            >
              <span style={{ fontSize: '1.3rem', lineHeight: 1 }}>{c.cardEmoji}</span>
              <span style={{ fontSize: '0.62rem', fontWeight: selected ? 600 : 400, color: selected ? 'white' : 'rgba(255,255,255,0.55)', lineHeight: 1.2, textAlign: 'center', fontFamily: 'inherit' }}>
                {c.name}
              </span>
              <span style={{ fontSize: '0.52rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1.2, textAlign: 'center' }}>
                {c.subtitle}
              </span>
            </button>
          )
        })}
      </div>

      {/* ── Daily goal ── */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ fontSize: '0.82rem', color: 'var(--bark)', whiteSpace: 'nowrap' }}>Daily goal:</label>
        <select value={data.goal} onChange={handleGoalChange} style={{ padding: '5px 10px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.15)', background: 'var(--surf)', color: 'var(--ink)', fontSize: '0.82rem', fontFamily: 'inherit', cursor: 'pointer' }}>
          {GOAL_OPTIONS.map(g => <option key={g} value={g}>{g.toLocaleString()} steps</option>)}
        </select>
      </div>

      {/* Today's progress */}
      <div style={{ marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
          <span style={{ fontSize: '0.72rem', color: 'var(--stone)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Today with {companion.name}</span>
          <span style={{ fontSize: '0.82rem', color: 'var(--bark)', fontWeight: 500 }}>{todaySteps.toLocaleString()} / {data.goal.toLocaleString()}{goalPct >= 100 && ' ✓'}</span>
        </div>
        <div className="walk-goal-bar-wrap">
          <div className="walk-goal-bar-fill" style={{ width: `${goalPct}%` }} />
        </div>
        {goalPct >= 100 && <p style={{ fontSize: '0.78rem', color: 'var(--accent)', marginTop: '0.3rem', fontStyle: 'italic' }}>{companion.goalMsg}</p>}
      </div>

      {/* Log walk */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <input type="number" value={stepInput} onChange={e => setStepInput(e.target.value)} placeholder="Steps (optional)" min="0"
          style={{ flex: '1', minWidth: '120px', padding: '7px 12px', border: '1px solid rgba(0,0,0,0.15)', borderRadius: '10px', background: 'var(--surf)', color: 'var(--ink)', fontSize: '0.88rem', fontFamily: 'inherit' }}
        />
        <button className="save-btn ready" onClick={() => logWalk(stepInput ? parseInt(stepInput, 10) : 0)} style={{ whiteSpace: 'nowrap' }}>
          🚶 I went for a walk
        </button>
      </div>

      {/* ── Adventure corridor ── */}
      <div className="dungeon-wrap" style={{ background: companion.bg }}>
        <div className="dungeon-flames">
          <span>{companion.leftIcon}</span>
          <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.38)', letterSpacing: '0.08em', alignSelf: 'center', textAlign: 'center' }}>{companion.title}</span>
          <span>{companion.rightIcon}</span>
        </div>

        <div className="dungeon-prize-row">
          {companion.prizes.map((prize, i) => {
            const pct = (STEP_MILESTONES[i] / MAX_STEPS) * 100
            const unlocked = totalSteps >= STEP_MILESTONES[i]
            return (
              <span key={i} className={`dungeon-prize-pin ${unlocked ? 'unlocked' : 'locked'}`} style={{ left: `${pct}%` }} title={`${prize.name} — ${STEP_MILESTONES[i].toLocaleString()} steps`}>
                {prize.emoji}
              </span>
            )
          })}
        </div>

        <div className="dungeon-track-wrap">
          <div className="dungeon-carl" style={{ left: `${corridorPct}%` }}>
            <span style={{ display: 'inline-block', transform: companion.flipRunner ? 'scaleX(-1)' : 'none' }}>
              {companion.runnerEmoji}
            </span>
          </div>
          <div className="dungeon-track">
            <div className="dungeon-fill" style={{ width: `${corridorPct}%`, background: companion.track }} />
          </div>
        </div>

        <div className="dungeon-totals">
          <div>
            <div className="dungeon-steps-big">{totalSteps.toLocaleString()}</div>
            <div className="dungeon-steps-label">steps with {companion.name}</div>
          </div>
          {nextPrize && nextMilestone ? (
            <div className="dungeon-next-label">
              {(nextMilestone - totalSteps).toLocaleString()} steps until<br />
              <strong style={{ color: 'rgba(255,255,255,0.8)' }}>{nextPrize.emoji} {nextPrize.name}</strong>
            </div>
          ) : (
            <div className="dungeon-next-label" style={{ color: 'gold' }}>🎉 Adventure complete!</div>
          )}
        </div>
      </div>

      {/* Loot toggle */}
      <button className="ghost-btn" onClick={() => setShowPrizes(p => !p)} style={{ fontSize: '0.82rem', marginTop: '0.75rem', marginBottom: showPrizes ? '0.75rem' : 0 }}>
        {showPrizes ? '▲ Hide loot' : '▼ Show loot collection'}
      </button>

      {showPrizes && (
        <div className="prize-collection">
          {companion.prizes.map((prize, i) => {
            const unlocked = totalSteps >= STEP_MILESTONES[i]
            return (
              <div key={i} className={`prize-item ${unlocked ? '' : 'locked'}`}>
                <span className="prize-item-emoji">{prize.emoji}</span>
                <div className="prize-item-body">
                  <div className="prize-item-name">{prize.name}</div>
                  <div className="prize-item-desc">{prize.desc}</div>
                </div>
                <div className="prize-item-steps">{unlocked ? '✓ collected' : `${STEP_MILESTONES[i].toLocaleString()} steps`}</div>
              </div>
            )
          })}
        </div>
      )}

    </Card>
  )
}
